import React, { useState, useEffect } from 'react';
import { CreditCard, Copy, Check } from 'lucide-react';
import paymentSettingsService from '../../services/paymentSettingsService';
import LoadingSpinner from '../LoadingSpinner';

const PaymentInstructionsModal = ({ isOpen, onClose, campaignId, commissionAmount }) => {
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const settings = await paymentSettingsService.getPaymentSettings();
        setPaymentSettings(settings);
      } catch (err) {
        console.error('Error fetching payment settings:', err);
        setError('Failed to load payment instructions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      fetchPaymentSettings();
    }
  }, [isOpen]);
  
  const handleCopyAccountNumber = () => {
    if (paymentSettings?.accountNumber) {
      navigator.clipboard.writeText(paymentSettings.accountNumber);
      setCopied(true);
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };
  
  const generateReferenceCode = (id) => {
    if (!id) return '';
    // Create a more professional reference code format
    // Format: EM-YYYY-NNNN where YYYY is current year and NNNN is a numeric part based on campaign ID
    const currentYear = new Date().getFullYear();
    
    // Convert the first 4 chars of campaign ID to a numeric value
    let numericPart = '';
    for (let i = 0; i < 4 && i < id.length; i++) {
      // Get character code and use modulo to ensure it's 0-9
      const charCode = id.charCodeAt(i) % 10;
      numericPart += charCode;
    }
    
    // Pad with zeros if needed
    while (numericPart.length < 4) {
      numericPart = '0' + numericPart;
    }
    
    return `EM-${currentYear}-${numericPart}`;
  };
  
  const handleCopyReference = () => {
    if (campaignId) {
      const referenceCode = generateReferenceCode(campaignId);
      navigator.clipboard.writeText(referenceCode);
      setCopied(true);
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary">
              <CreditCard className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Payment Instructions
              </h3>
              
              <div className="mt-4">
                {loading ? (
                  <div className="flex justify-center py-4">
                    <LoadingSpinner size="md" color="primary" />
                  </div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-red-600">{error}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-100 rounded-md p-4">
                      <p className="text-blue-800 font-medium">
                        Please transfer ₱{commissionAmount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} to the account below
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-md p-4 space-y-3">
                      <div>
                        <p className="text-sm text-gray-500">Bank Name</p>
                        <p className="font-medium">{paymentSettings?.bankName}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Account Name</p>
                        <p className="font-medium">{paymentSettings?.accountName}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Account Number</p>
                        <div className="flex items-center">
                          <p className="font-medium">{paymentSettings?.accountNumber}</p>
                          <button
                            type="button"
                            onClick={handleCopyAccountNumber}
                            className="ml-2 p-1 rounded-md hover:bg-gray-200"
                            title="Copy account number"
                          >
                            {copied ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500">Reference Code (Important)</p>
                        <div className="flex items-center">
                          <p className="font-medium">{campaignId ? generateReferenceCode(campaignId) : 'Will be generated after submission'}</p>
                          {campaignId && (
                            <button
                              type="button"
                              onClick={handleCopyReference}
                              className="ml-2 p-1 rounded-md hover:bg-gray-200"
                              title="Copy reference code"
                            >
                              {copied ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-500" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-left">
                      <p className="font-medium text-sm mb-2">Additional Instructions:</p>
                      <p className="text-sm text-gray-600">{paymentSettings?.instructions}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-5 sm:mt-6">
            <button
              type="button"
              className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
              onClick={onClose}
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentInstructionsModal;
